import { motion, useReducedMotion } from "motion/react";
import { useLayoutEffect, useState } from "react";
import { WIZARD_GLIDE } from "./wizardMotion";

type Rect = { top: number; left: number; width: number; height: number };

const sameRect = (a: Rect | null, b: Rect | null): boolean =>
  a === null || b === null
    ? a === b
    : a.top === b.top &&
      a.left === b.left &&
      a.width === b.width &&
      a.height === b.height;

// The box of the first element that matches, in viewport coordinates, with a
// small margin so the wash does not touch the target's edge. Null when the
// page does not show the target (yet).
function measure(selector: string): Rect | null {
  const element = document.querySelector(selector);
  if (element === null) return null;
  const box = element.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) return null;
  const margin = 6;
  return {
    top: box.top - margin,
    left: box.left - margin,
    width: box.width + 2 * margin,
    height: box.height + 2 * margin,
  };
}

/**
 * The focus of a note of the wizard (docs/juiscigpt-wizard.md REQ-16): the
 * page is blurred except its target, which carries a green wash and an anchor
 * dot. Without a target on the page, the whole page is blurred. A tap on the
 * blur calls `onTapOutside`; the target itself stays usable.
 *
 * UI machinery: the box follows the target through scrolls, keyboard moves
 * and a growing answer, so it is measured on each animation frame.
 */
export function WizardSpotlight({
  targetSelector,
  onTapOutside,
}: {
  targetSelector: string | null;
  onTapOutside: () => void;
}): React.ReactElement {
  const reducedMotion = useReducedMotion() === true;
  const [rect, setRect] = useState<Rect | null>(null);

  useLayoutEffect(() => {
    if (targetSelector === null) {
      setRect(null);
      return;
    }
    let frame = 0;
    const follow = (): void => {
      const next = measure(targetSelector);
      setRect((previous) => (sameRect(previous, next) ? previous : next));
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [targetSelector]);

  const blur = "fixed z-40 bg-ds-cream/20 backdrop-blur-sm";
  const appear = {
    initial: reducedMotion ? false : { opacity: 0 },
    animate: { opacity: 1 },
    transition: reducedMotion ? { duration: 0 } : WIZARD_GLIDE,
  } as const;

  if (rect === null)
    return (
      <motion.div
        {...appear}
        aria-hidden="true"
        className={`${blur} inset-0`}
        onClick={onTapOutside}
      />
    );

  const bottom = rect.top + rect.height;
  const right = rect.left + rect.width;
  return (
    <>
      <motion.div
        {...appear}
        aria-hidden="true"
        className={`${blur} inset-x-0 top-0`}
        style={{ height: Math.max(rect.top, 0) }}
        onClick={onTapOutside}
      />
      <motion.div
        {...appear}
        aria-hidden="true"
        className={`${blur} inset-x-0 bottom-0`}
        style={{ top: bottom }}
        onClick={onTapOutside}
      />
      <motion.div
        {...appear}
        aria-hidden="true"
        className={`${blur} left-0`}
        style={{
          top: rect.top,
          height: rect.height,
          width: Math.max(rect.left, 0),
        }}
        onClick={onTapOutside}
      />
      <motion.div
        {...appear}
        aria-hidden="true"
        className={`${blur} right-0`}
        style={{ top: rect.top, height: rect.height, left: right }}
        onClick={onTapOutside}
      />
      <motion.div
        {...appear}
        aria-hidden="true"
        className="pointer-events-none fixed z-40 rounded-2xl bg-ds-mint-400/15 ring-2 ring-ds-mint-400"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
      >
        <span className="absolute -right-1 -top-1 size-3 rounded-full bg-ds-mint-500 ring-2 ring-ds-cream" />
      </motion.div>
    </>
  );
}
